import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@accure/ui';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

import { SiteOperationalPhase } from '@/api/fe-api/domain/entities/site-operational-phase';
import { GetAllSitesExtended } from '@/api/fe-api/view/queries/get-all-sites-extended';
import { PageUrlKey } from '@/entities/page-url-key';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { cls } from '@/utils/cls';
import { siteLabel } from '@/utils/format/customer';

export function SiteSelection() {
  const { asset, isLoading, assetId } = useCurrentAsset();
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<DOMRect | undefined>(undefined);

  useEffect(() => {
    if (menuRef.current) {
      setCoords(menuRef.current.getBoundingClientRect());
    }
  }, []);

  const menu = createPortal(
    <DropdownMenu
      anchor="top"
      data-testid="site-selection-menu"
      className="mt-2 max-h-[80vh] !bg-white !ring-zinc-950/10"
      style={{
        left: coords?.x,
        top: coords?.y,
      }}
    >
      <SiteSelectionList />
    </DropdownMenu>,
    document.body,
  );

  return (
    <Dropdown>
      <DropdownButton
        data-testid="site-selection-button"
        className="flex min-h-10 w-full justify-between"
        color={assetId ? 'zinc' : 'white'}
      >
        {isLoading ? (
          <span
            className={cls('font-normal text-zinc-500', {
              'text-zinc-300': Boolean(assetId),
            })}
          >
            {'Loading\u22EF'}
          </span>
        ) : (
          <>
            <div className="whitespace-nowrap">
              {asset?.fullFrontendId
                ? siteLabel(asset.fullFrontendId)
                : 'Portfolio'}
            </div>
            <ChevronDownIcon />
          </>
        )}
      </DropdownButton>
      <div ref={menuRef}>{menu}</div>
    </Dropdown>
  );
}

function SiteSelectionList() {
  const allSites = GetAllSitesExtended.useQuery();
  const location = useLocation();
  const history = useHistory();

  const handleSiteSelection = (siteId: string) => {
    const currentPath = location.pathname;

    // Check if we're already on an asset page
    const assetPathMatch = currentPath.match(/^\/asset\/([^/]+)(?:\/(.*))?$/);
    if (assetPathMatch) {
      // Replace the existing siteId in the URL, preserving the rest of the path
      const [, , remainingPath] = assetPathMatch;
      const newPath = remainingPath
        ? `/asset/${siteId}/${remainingPath}`
        : `/asset/${siteId}`;
      history.replace(newPath);
      return;
    }

    // Navigate to the current path with the selected siteId
    const pathWithoutLeadingSlash = currentPath.replace(/^\//, '');
    const normalizedPath = pathWithoutLeadingSlash.replace(
      PageUrlKey.PROJECTS,
      PageUrlKey.PROJECT,
    );

    // Handle empty path case (e.g., when on root '/')
    if (!normalizedPath) {
      history.push(`/asset/${siteId}`);
      return;
    }

    const newPath = generatePath('/asset/:siteId/:path*', {
      siteId,
      path: normalizedPath,
    });
    history.push(newPath);
  };

  if (allSites.isLoading) {
    return (
      <DropdownItem className="!text-zinc-500">{'Loading\u22EF'}</DropdownItem>
    );
  }

  return (
    <>
      {allSites?.data?.map((site) => {
        const isMonitoringEnded =
          site.operationalPhase === SiteOperationalPhase.MONITORING_ENDED;
        return (
          <DropdownItem
            key={site.id}
            data-testid="site-selection-item"
            onClick={() => handleSiteSelection(site.id)}
            className={cls(
              'cursor-pointer !text-zinc-950 hover:bg-zinc-950/5',
              { 'opacity-50': isMonitoringEnded },
            )}
          >
            {siteLabel(site.fullFrontendId)}
          </DropdownItem>
        );
      })}
    </>
  );
}
